import { Box, Button, ConfirmDialog, Flex, Menu, MenuDivider, MenuItem, Popover, Text } from 'core/components';
import VariableTag from 'app/views/boards/VariableTag';
import { Field, Switch } from 'core/form';
import { useFormContext } from 'core/form/FormContext';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import BoardVariableDialog from 'app/views/boards/BoardVariableDialog';
import {
  getGuidedDimensionOptions,
  GUIDED_DIMENSION_TYPE
} from '@kentik/ui-shared/query/measurements/guidedDimensions';
import {
  getAssetTagDimensionKey,
  ASSET_TAG_CATEGORY_LABEL
} from '@kentik/ui-shared/dictionary/v2/compilers/assetTagMeasurements';
import { generateUniqueVariableName } from '@kentik/ui-shared/boards/boards';
import { useAssetTags } from 'app/stores/dictionary/useAssetTags';
import { useCustomDimensions } from 'app/stores/dictionary/useCustomDimensions';

const BoardVariableSettings = observer(({ board, onMapVariable }) => {
  const form = useFormContext();
  const { data: assetTags = [] } = useAssetTags();
  const { data: customDimensions = [] } = useCustomDimensions();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [confirmRemoveIndex, setConfirmRemoveIndex] = useState(-1);
  const [editingVariable, setEditingVariable] = useState(null);

  const variablesField = form.getField('config.variables');

  const supportedGuidedTypes = Object.values(GUIDED_DIMENSION_TYPE).filter((guidedType) =>
    (board.panelCollection.models || []).some((panel) => {
      const measurement = panel.getMeasurement();
      if (!measurement) {
        return false;
      }
      return getGuidedDimensionOptions(guidedType, measurement).length > 0;
    })
  );

  const guidedOptions = supportedGuidedTypes.map((name) => ({ name }));

  const assetTagOptions = assetTags.map((tag) => ({
    name: tag.display_name,
    label: tag,
    filterField: getAssetTagDimensionKey(tag.name)
  }));
  const propagateChange = () => {
    // Trigger form onChange so this stuff is seen and propagated
    if (form.onChange) {
      form.onChange({
        form,
        formValues: form.getValues(),
        field: variablesField,
        fieldValue: variablesField.getValue()
      });
    }
  };

  const handleAddCustomVariable = () => {
    const editingVar = variablesField.buildFieldState({
      type: 'custom',
      name: '',
      value: null,
      allowMulti: false,
      exactMatch: false,
      options: [{ label: '', value: '' }]
    });
    setIsDialogOpen(true);
    setEditingIndex(-1);
    setEditingVariable(editingVar);
  };

  const handleAddGuidedVariable = ({ name, filterField }) => {
    // The original `name` is the guided type identity (e.g. 'Site', a device-tag display name).
    // Persist it as `guidedType` so the display `name` can be renamed / made unique independently.
    const guidedType = name;
    // Generate a unique display name so multiple variables of the same type can coexist.
    const uniqueName = generateUniqueVariableName(
      name,
      variablesField.fieldStates.map((fs) => fs.name.value)
    );
    const fieldStates = variablesField.fieldStates.slice();
    fieldStates.push(
      variablesField.buildFieldState({
        type: 'guided',
        guidedType,
        name: uniqueName,
        field: filterField,
        value: null,
        allowMulti: true,
        exactMatch: true,
        options: [],
        operator: '='
      })
    );
    variablesField.setFieldStates(fieldStates);

    board.panelCollection.models.forEach((panel) => {
      const measurement = panel.getMeasurement();
      if (!measurement) {
        return;
      }
      const field = filterField ?? getGuidedDimensionOptions(guidedType, measurement)[0];
      if (field) {
        panel.setVariableMapping({ variableName: uniqueName, filterField: field });
      }
    });

    propagateChange();
  };

  const handleEditVariable = (index) => {
    const editingVar = variablesField.buildFieldState(variablesField.getValue()[index]);
    setIsDialogOpen(true);
    setEditingIndex(index);
    setEditingVariable(editingVar);
  };

  const renameVariableMappings = (oldName, newName) => {
    board.panelCollection.models.forEach((panel) => {
      const config = panel.get('config');
      const variableMappings = config.variableMappings || [];
      if (variableMappings.some((mapping) => mapping.variableName === oldName)) {
        const updated = variableMappings.map((mapping) =>
          mapping.variableName === oldName ? { ...mapping, variableName: newName } : mapping
        );
        panel.safeSet({ config: { ...config, variableMappings: updated } });
        panel.save({}, { draft: true });
      }
    });
  };

  const handleDialogSave = () => {
    if (editingVariable) {
      const fieldStates = variablesField.fieldStates.slice();
      if (editingIndex > -1) {
        const oldName = fieldStates[editingIndex].name.value;
        const newName = editingVariable.name.value;
        fieldStates[editingIndex] = editingVariable;
        if (oldName && newName && oldName !== newName) {
          renameVariableMappings(oldName, newName);
        }
      } else {
        fieldStates.push(editingVariable);
      }
      variablesField.setFieldStates(fieldStates);

      propagateChange();
    }
    setIsDialogOpen(false);
    setEditingVariable(null);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingVariable(null);
  };

  const handleRemoveVariable = (index, isCustom) => {
    if (isCustom) {
      setConfirmRemoveIndex(index);
    } else {
      form.getField('config.variables').remove(index);
    }
  };

  const handleConfirmRemove = () => {
    form.getField('config.variables').remove(confirmRemoveIndex);
    setConfirmRemoveIndex(-1);
  };

  const handleCancelRemove = () => {
    setConfirmRemoveIndex(-1);
  };

  return (
    <Flex flexDirection="column" gap={2} height="100%">
      <Flex gap={1}>
        <Popover
          content={
            <Menu style={{ maxHeight: '420px', overflowY: 'auto' }}>
              <MenuItem fontSize={12} text="QUICK FILTERS" disabled />
              {guidedOptions.map((opt) => (
                <MenuItem key={opt.name} text={opt.name} onClick={() => handleAddGuidedVariable(opt)} />
              ))}
              {assetTagOptions.length > 0 && (
                <MenuItem
                  text={ASSET_TAG_CATEGORY_LABEL}
                  submenuProps={{ style: { maxHeight: '300px', overflowY: 'auto' } }}
                >
                  {assetTagOptions.map((opt) => (
                    <MenuItem key={opt.filterField} text={opt.name} onClick={() => handleAddGuidedVariable(opt)} />
                  ))}
                </MenuItem>
              )}
              {customDimensions.length > 0 && (
                <MenuItem text="Custom Dimensions" submenuProps={{ style: { maxHeight: '300px', overflowY: 'auto' } }}>
                  {customDimensions.map((dim) => (
                    <MenuItem
                      key={dim.col_name}
                      text={dim.description || dim.col_name}
                      onClick={() =>
                        handleAddGuidedVariable({
                          name: `${dim.description || dim.col_name}`,
                          filterField: dim.col_name
                        })
                      }
                    />
                  ))}
                </MenuItem>
              )}
              <MenuDivider />
              <MenuItem
                key="custom"
                text={
                  <Flex flexDirection="column">
                    <Text>Custom Filter</Text>
                    <Text fontSize={12} color="muted">
                      Define a filter with custom conditions
                    </Text>
                  </Flex>
                }
                onClick={handleAddCustomVariable}
              />
            </Menu>
          }
          position="bottom-left"
        >
          <Button text="Add Filter" icon="plus" rightIcon="caret-down" intent="primary" />
        </Popover>
      </Flex>

      {/* {!hasVariables && <Text muted>No filters have been created yet.</Text>} */}

      <Flex flexDirection="column" gap={1}>
        {variablesField.map((variable, index) => {
          const variableName = variable.name.value;
          const isLast = index === variablesField.fieldStates.length - 1;

          return (
            <Flex key={variable.name._id} gap="2px" alignItems="center" borderBottom={isLast ? '' : 'thin'} pb="6px">
              <Box flex={1}>
                <Flex gap={1} alignItems="center" justifyContent="space-between">
                  <Flex alignItems="center">
                    <Field field={variable.enabled} showLabel={false} m={0}>
                      <Switch showSwitchLabel={false} />
                    </Field>
                    {variable.type.value === 'custom' ? (
                      <VariableTag name={variableName} />
                    ) : (
                      <VariableTag name={variableName} icon={null} asTag={false} iconColor="muted" />
                    )}
                  </Flex>
                  <Button
                    rightIcon="random"
                    text="Map to panels"
                    onClick={() => onMapVariable(variable)}
                    small
                    minimal
                  />
                </Flex>
              </Box>
              <Popover
                position="bottom-right"
                content={
                  <Menu>
                    <MenuItem
                      icon="edit"
                      text={variable.type.value === 'guided' ? 'Rename' : 'Edit'}
                      onClick={() => handleEditVariable(index)}
                    />
                    <MenuDivider />
                    <MenuItem
                      icon="trash"
                      text="Remove"
                      intent="danger"
                      onClick={() => handleRemoveVariable(index, variable.type.value === 'custom')}
                    />
                  </Menu>
                }
              >
                <Button icon="more" small minimal aria-label="More options" />
              </Popover>
            </Flex>
          );
        })}
      </Flex>

      <BoardVariableDialog
        isOpen={isDialogOpen}
        isNew={editingIndex < 0}
        onClose={handleDialogClose}
        onSave={handleDialogSave}
        variable={editingVariable}
        existingNames={variablesField
          .getValue()
          .filter((_, i) => i !== editingIndex)
          .map((v) => v.name)}
      />
      <ConfirmDialog
        isOpen={confirmRemoveIndex >= 0}
        title="Remove Custom Filter"
        text="Are you sure you want to remove this custom filter? This will also remove it from any panel mappings."
        confirmButtonText="Remove"
        confirmButtonIntent="danger"
        onConfirm={handleConfirmRemove}
        onCancel={handleCancelRemove}
      />
    </Flex>
  );
});

export default BoardVariableSettings;
